import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import { minutesToHours } from 'date-fns';
import { camelCase } from 'lodash';
import { ErrorMessage } from '../../forms/js/global';

export const TypeRadioBoxFields = ({ name, options, selectedOptions, paidEmpUsed, onChangeEvent, error }) => {
    const editItemOriginal = useRef(selectedOptions[0]);

    const onChange = (e) => {
        const { checked, value } = e.target;
        const newSelections = checked ? [value] : null;
        onChangeEvent(name, newSelections);
    }
    
    const paidEmpUsedInHours = (paidEmpUsed / 60);
    const paidEmpUsedInHoursReadible = Math.floor(paidEmpUsedInHours);
    const paidEmpUsedInMinsReadible = paidEmpUsed - (paidEmpUsedInHoursReadible * 60);

    let paidHoursText = paidEmpUsedInHoursReadible > 1 ? paidEmpUsedInHoursReadible + 'hrs ' : paidEmpUsedInHoursReadible + 'hr ';
    if(paidEmpUsedInHoursReadible === 0) paidHoursText = '';
    let paidMinsText = paidEmpUsedInMinsReadible > 1 ? paidEmpUsedInMinsReadible + 'mins ' : paidEmpUsedInMinsReadible + 'min ';
    if(paidEmpUsedInMinsReadible === 0) paidMinsText = '';

    const editCurrentItemIsPaidEmp = editItemOriginal.current === "Paid employment";
    const additionalEditText = editCurrentItemIsPaidEmp ? '(including this experience)' : '';

    const optionsFields = options.map((option, i) => {
        const inputId = camelCase(option);

        const disableRadio = option === "Paid employment" && paidEmpUsedInHours === 20 && !editCurrentItemIsPaidEmp;
        
        const isSelected = option === selectedOptions[0] ? 'checked' : null;

        return (
            <li key={i}>
                <input 
                    type="radio" 
                    name={name} 
                    id={inputId} 
                    value={option} 
                    defaultChecked={isSelected} 
                    onChange={onChange}
                    disabled={disableRadio}
                />
                <label htmlFor={inputId}>{option}</label>
            </li>
        )
    })

    return (
        <>
            <ul className="sq-form-question-option-list">
                {optionsFields}
            </ul>
            {error ? <ErrorMessage additionalText name={name} /> : null}
            {paidEmpUsedInHours === 20 ? <span className="sq-form-question-note">You have currently  entered a total of 20 hours of paid employment{additionalEditText}. We encourage you to try a range of different work experiences.</span> : null}
            {paidEmpUsedInHours < 20 && selectedOptions[0] === "Paid employment" ? <span className="sq-form-question-note">You can add up to a total of 20 hours of paid employment. Current total{additionalEditText}: {paidHoursText} {paidMinsText}</span> : null}
        </>
    )
}

export const RadioCustomIconLabelFields = ({ name, options, selectedOptions, onChangeEvent, error}) => {

    const onChange = (e) => {
        const { checked, value } = e.target;
        const newSelections = checked ? value : null;
        onChangeEvent(name, newSelections);
    }

    const optionsFields = options.map((option, i) => {
        const inputId = camelCase(option.text);
        const isSelected = selectedOptions === option.text ? 'checked' : null;

        return (
            <li key={i}>
                <label htmlFor={inputId}>
                    <input 
                        type="radio" 
                        name={name} 
                        id={inputId} 
                        value={option.text} 
                        defaultChecked={isSelected} 
                        onChange={onChange}
                    />
                    <span className="sr-only">{option.text}</span>
                    <i className={'far ' + option.icon} />
                </label>
            </li>
        )
    })

    return (
        <>
            <ul className={"custom-label__icon"}>
                {optionsFields}
            </ul>
            {error ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

export const TimeCaptureField = ({ name, value, required, selectedOptions, paidEmpUsed, onChangeEvent, error }) => {
    const inputTime = useMemo(() => convertMinutesToFormFriendly(value), [value]);
    const editItemTime = useRef(selectedOptions.hoursCompleted);

    const isPaidEmployment = selectedOptions.expType[0] === 'Paid employment';
    
    const formatChars = isPaidEmployment ? {
        '1': '[0-2]',
        '2': '[0-9]',
        '3': '[0-5]',
        '4': '[0-9]'
    } :
    {
        '1': '[0-4]',
        '2': '[0-9]',
        '3': '[0-5]',
        '4': '[0-9]'
    }
    ;
    const currentSelectedTotal = editItemTime.current || 0;
    const paidEmpHoursLeft = Math.floor((1200 - (paidEmpUsed - currentSelectedTotal)) / 60);
    const paidEmpMinutesLeft = (1200 - (paidEmpUsed - currentSelectedTotal)) - (paidEmpHoursLeft * 60);

    const currentSelectedHours = Math.floor(selectedOptions.hoursCompleted / 60) || 0;

    const currentSelectedMinutes = selectedOptions.hoursCompleted - (paidEmpHoursLeft * 60) || 0;

    // console.log(paidEmpHoursLeft, paidEmpMinutesLeft);
    // console.log(currentSelectedHours);

    const beforeMaskedValueChange = (newState) => {
        const { value } = newState;
        // console.log(newState)
        // Paid employment default
        if(isPaidEmployment && value.startsWith('2')) {
            formatChars['2'] = '[0]'; // To block 20+
            formatChars['3'] = '[0]';
            formatChars['4'] = '[0]';
        }

        // Paid employment. Calulate whats left over
        if(isPaidEmployment && paidEmpUsed > 0) {
            const paidEmpHoursLeftLocale = paidEmpHoursLeft.toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
            });
            const currentSelectedHoursLocale = currentSelectedHours.toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
            });

            const currentSelectedMinutesLocale = currentSelectedMinutes.toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
            });
            
            const secondDigit = paidEmpHoursLeftLocale[0] === "1" && currentSelectedHoursLocale[0] === '0' ? 9 : paidEmpHoursLeftLocale.toString()[1];

            const thirdDigit = paidEmpHoursLeft === currentSelectedHours ? paidEmpMinutesLeft.toString()[0] : 5;

            const forthDigit = paidEmpHoursLeft === currentSelectedHours && paidEmpMinutesLeft.toString()[0] === currentSelectedMinutesLocale[0] ? paidEmpMinutesLeft.toString()[1] : 9;

            formatChars['1'] = paidEmpHoursLeft < 10 ? '[0]' : '[0-1]';
            formatChars['2'] = '[0-' + secondDigit + ']';
            formatChars['3'] = '[0-' + thirdDigit + ']';
            formatChars['4'] = '[0-' + forthDigit + ']';
        }

        // All other options
        if(!isPaidEmployment && value.startsWith('4')) {
            // console.log('hi')
            formatChars['2'] = '[0]'; // To block 40+
            formatChars['3'] = '[0]';
            formatChars['4'] = '[0]';
        }
        return {value, selection: newState.selection};
    }

    const onChange = (e) => {
        const hoursAndMin = e.target.value.replace('_', '0').split(':');
        const newTime = parseInt(hoursAndMin[0] * 60) + parseInt(hoursAndMin[1]);
        onChangeEvent(name, newTime);
    }

    let hoursText = paidEmpHoursLeft > 1 ? paidEmpHoursLeft + 'hrs ' : paidEmpHoursLeft + 'hr ';
    if(paidEmpHoursLeft === 0) hoursText = '';
    let minsText = paidEmpMinutesLeft > 1 ? paidEmpMinutesLeft + 'mins ' : paidEmpMinutesLeft + 'min ';
    if(paidEmpMinutesLeft === 0) minsText = '';

    const fieldNote = !isPaidEmployment ? "max: 40 hours" : "max: " + hoursText + minsText;

    return (
        <>
            <InputMask 
                name={name}
                defaultValue={inputTime}
                id={name} 
                className="sq-form-field" 
                mask={"12:34"}
                formatChars={formatChars}
                type="text"
                required={required}
                alwaysShowMask="true"
                onChange={onChange}
                beforeMaskedValueChange={beforeMaskedValueChange}
            />
            <em className="sq-form-question-note time-capture-note">{fieldNote}</em>
            {error && (inputTime.toString() === '00:00' || Number.isNaN(value)) ? <ErrorMessage additionalText name={name} /> : null}
        </>
    )
}

const convertMinutesToFormFriendly = (number) => {
    const howManyHours = minutesToHours(number);
    const hoursIn24Format = howManyHours.toString().length === 1 ? '0' + howManyHours : howManyHours;
    const leftoverMinutes = number - (howManyHours * 60);
    const minutesIn24Format = leftoverMinutes.toString().length === 1 ? '0' + leftoverMinutes : leftoverMinutes;

    const humanReadibleTime = hoursIn24Format + ':' + minutesIn24Format;

    return humanReadibleTime;
}


// PropTypes
TypeRadioBoxFields.propTypes = { 
    name: PropTypes.string, 
    options: PropTypes.array,
    selectedOptions: PropTypes.array,
    paidEmpUsed: PropTypes.number,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

RadioCustomIconLabelFields.propTypes = { 
    name: PropTypes.string, 
    options: PropTypes.array, 
    selectedOptions: PropTypes.string,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}

TimeCaptureField.propTypes = {
    name: PropTypes.string, 
    value: PropTypes.number,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    selectedOptions: PropTypes.object,
    paidEmpUsed: PropTypes.number,
    onChangeEvent: PropTypes.func, 
    error: PropTypes.bool
}